if (!customElements.get('store-locations-carousel')) {
  customElements.define(
    'store-locations-carousel',
    class StoreLocationsCarousel extends HTMLElement {
      connectedCallback() {
        if (this.isInitialized) return;

        this.viewport = this.querySelector('[data-store-carousel-viewport]');
        this.track = this.querySelector('[data-store-carousel-track]');
        this.slides = Array.from(this.querySelectorAll('[data-store-carousel-slide]'));
        this.status = this.querySelector('[data-store-carousel-status]');

        if (!this.viewport || !this.track || this.slides.length < 2) return;

        this.isInitialized = true;
        this.currentIndex = 0;
        this.pointerStartX = null;
        this.mobileQuery = window.matchMedia('(max-width: 749px)');

        this.handlePrevious = () => this.goTo(this.currentIndex - 1);
        this.handleNext = () => this.goTo(this.currentIndex + 1);
        this.handleKeydown = (event) => {
          if (!this.mobileQuery.matches || event.target !== this) return;
          if (event.key === 'ArrowLeft') this.handlePrevious();
          if (event.key === 'ArrowRight') this.handleNext();
        };
        this.handlePointerDown = (event) => {
          if (!this.mobileQuery.matches || !event.isPrimary) return;
          this.pointerStartX = event.clientX;
        };
        this.handlePointerUp = (event) => {
          if (this.pointerStartX === null || !event.isPrimary) return;

          const distance = event.clientX - this.pointerStartX;
          const threshold = this.viewport.clientWidth * 0.12;
          this.pointerStartX = null;

          if (Math.abs(distance) < threshold) return;
          if (distance < 0) this.handleNext();
          if (distance > 0) this.handlePrevious();
        };
        this.handlePointerCancel = () => {
          this.pointerStartX = null;
        };
        this.handleModeChange = () => this.updateMode();

        this.addEventListener('keydown', this.handleKeydown);
        this.viewport.addEventListener('pointerdown', this.handlePointerDown);
        this.viewport.addEventListener('pointerup', this.handlePointerUp);
        this.viewport.addEventListener('pointercancel', this.handlePointerCancel);

        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener('change', this.handleModeChange);
        } else {
          this.mobileQuery.addListener(this.handleModeChange);
        }

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.updatePosition());
          this.resizeObserver.observe(this.viewport);
        }

        this.updateMode();
      }

      disconnectedCallback() {
        if (!this.isInitialized) return;

        this.removeEventListener('keydown', this.handleKeydown);
        this.viewport.removeEventListener('pointerdown', this.handlePointerDown);
        this.viewport.removeEventListener('pointerup', this.handlePointerUp);
        this.viewport.removeEventListener('pointercancel', this.handlePointerCancel);

        if (this.mobileQuery.removeEventListener) {
          this.mobileQuery.removeEventListener('change', this.handleModeChange);
        } else {
          this.mobileQuery.removeListener(this.handleModeChange);
        }

        this.resizeObserver?.disconnect();
      }

      updateMode() {
        if (this.mobileQuery.matches) {
          this.setAttribute('tabindex', '0');
          this.setAttribute('data-carousel-ready', '');
          this.goTo(Math.min(this.currentIndex, this.slides.length - 1));
          return;
        }

        this.setAttribute('tabindex', '-1');
        this.removeAttribute('data-carousel-ready');
        this.track.style.removeProperty('transform');
        this.slides.forEach((slide) => {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        });
      }

      goTo(index) {
        if (!this.mobileQuery.matches) return;

        this.currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
        this.updatePosition();
        this.updateAccessibility();
      }

      updatePosition() {
        if (!this.mobileQuery.matches || !this.hasAttribute('data-carousel-ready')) return;

        const activeSlide = this.slides[this.currentIndex];
        if (!activeSlide) return;

        const layoutOffset = activeSlide.offsetLeft - this.slides[0].offsetLeft;
        const maximumOffset = Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);
        const offset = Math.min(Math.max(0, layoutOffset), maximumOffset);

        this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      }

      updateAccessibility() {
        this.slides.forEach((slide, index) => {
          const isActive = index === this.currentIndex;
          slide.toggleAttribute('inert', !isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        if (this.status) {
          this.status.textContent = `Store ${this.currentIndex + 1} of ${this.slides.length}`;
        }
      }
    }
  );
}
